/**
 * The presence predicate for workflow node configuration — ONE definition, read by
 * the canvas validators and by the backend's `computeConfigStatus` alike.
 *
 * "Is this node runnable" is answered twice: by 44 per-node validators in
 * `apps/frontend/src/modules/Workflow/validation/` (the browser's pre-publish gate)
 * and by `apps/backend/src/workflow/utils/compute-config-status.ts` (the public
 * `validate` endpoint, the overview, and the publish gate). Those two answer
 * DIFFERENT questions on purpose. They are not allowed to differ about what
 * "configured" MEANS — a field the canvas calls empty and the backend calls filled
 * makes the same graph both publishable and unpublishable depending on which door
 * it came through.
 *
 * So the vocabulary lives here, in the one package both sides already depend on:
 * {@link hasValue} and {@link isParameterFilled} are the predicate, and
 * {@link REQUIRED_NODE_FIELDS} is the set of per-node rules the two sides have
 * settled. Whether a rule belongs in that table is a product question; where it
 * lives once settled is not.
 */

/**
 * True when a node field holds something a run could actually use.
 *
 * The semantics are the canvas's, because 43 of its 44 validators already used
 * them and the backend used raw JS falsiness:
 *
 * - `null` / `undefined` — empty.
 * - a string — empty when it TRIMS to nothing, so `"   "` does not count as filled.
 * - an array — empty when it has no elements.
 * - any other object — empty when it has no own enumerable keys, so `{}` is empty.
 * - anything else, INCLUDING `0` and `false` — present. A numeric zero and a
 *   boolean false are configured values, not missing ones; raw falsiness cannot
 *   tell them apart from an unset field and reports both as missing.
 */
export function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

/** Narrow an unknown to a plain keyed object. Arrays are excluded. */
function toRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * True when a `parametersSetup` entry will actually yield a value at run time.
 *
 * The key merely EXISTING is not enough: an entry is a `ParameterSetup` value, so
 * `handler: "manual"` with a blank `value` — or `handler: "variable"` with a blank
 * `variable` — is still truthy while resolving to nothing.
 *
 * `handler: "prompt"` counts as filled: the agent supplies the value mid-run, so
 * there is nothing for the author to configure. An unrecognised handler is treated
 * the same way rather than reported missing — this runs on user-supplied JSON from
 * the public API, and a shape this function does not model is not evidence of an
 * unconfigured field.
 */
export function isParameterFilled(entry: unknown): boolean {
  const setup = toRecord(entry);
  if (!setup) return false;
  if (setup.handler === "manual") return hasValue(setup.value);
  if (setup.handler === "variable") return hasValue(setup.variable);
  return true;
}

/**
 * True when a `recordScore` node needs `chatIdSource` filled.
 *
 * `chatIdSource` resolves the conversation a score attaches to, so it matters only
 * when the score targets a conversation. For `workflowRun` the scorable id comes
 * from the execution context and `chatIdSource` is ignored.
 *
 * Takes `unknown` because both callers reach it with a value of a different
 * static type: the config panel holds a typed `RecordScoreTarget`, while
 * {@link REQUIRED_NODE_FIELDS} probes user-supplied node JSON. One declaration
 * serving both is the point — the canvas hiding the field while a validator still
 * demands it is the same class of split this module exists to close.
 */
export function scoreTargetRequiresChatId(scorableTarget: unknown): boolean {
  return scorableTarget === "conversation";
}

/** One field a node type cannot run without, and the message both doors report. */
export type RequiredNodeField = {
  /** Key in the node's `data`. Reported verbatim as a `missingFields` entry. */
  readonly field: string;
  /** Operator-facing reason, rendered by the canvas validator. */
  readonly message: string;
  /**
   * Restricts the requirement to the node data that actually needs it. Absent
   * means unconditional. Never reaches for anything outside `data`, so both
   * doors can evaluate it from the stored node alone.
   */
  readonly when?: (data: Record<string, unknown>) => boolean;
};

/**
 * The fields BOTH doors agree a node type cannot run without.
 *
 * The canvas raised an unconditional `critical` error on every entry below while
 * `computeConfigStatus` had no `case` for any of these node types at all — it fell
 * through `default: break` and reported them COMPLETE. Same graph, two verdicts:
 * the browser refused to publish it and the public `validate` endpoint called it
 * ready. The rules are declared once here and read structurally by both sides, so a
 * row added to this table reaches both surfaces without either being edited.
 *
 * SCOPE, AND WHY IT IS NOT WIDER. This table holds the direction where the CANVAS
 * was already stricter, which is the direction that cannot refuse anything new:
 * these node types are absent from `PUBLISH_BLOCKING_FIELDS`, so the backend reads
 * them advisory-only, and a graph carrying one of these gaps was already refused by
 * the browser. The opposite direction — roughly a dozen types where the BACKEND is
 * stricter than the canvas (`plugin.toolId`, `timer.seconds`, `branching.branches`,
 * `customScript.code`, …) — is a live product decision, because closing it means
 * tightening the canvas and refusing the RE-publish of workflows already live. It
 * is deliberately not in this table.
 *
 * WHAT STAYS OUT, ON PURPOSE. Rules that are not plain presence live with their
 * own surface: `platformListenerTrigger`'s idle threshold and filter-completeness
 * checks read nested config and produce bespoke messages, and `doWhile`'s
 * `maxIterations >= 1` is a numeric floor rather than a presence question.
 */
export const REQUIRED_NODE_FIELDS: Readonly<Record<string, readonly RequiredNodeField[]>> = {
  aiPassiveSatisfactionScore: [
    { field: "chatIdSource", message: "Chat to score is required" },
    { field: "instructions", message: "Analysis prompt is required" }
  ],
  newsMonitorTrigger: [
    { field: "monitorProvider", message: "A news provider must be selected" },
    { field: "query", message: "A search query is required" }
  ],
  nexusApi: [
    { field: "category", message: "Category is required" },
    { field: "action", message: "Action is required" }
  ],
  parseSatisfactionReply: [
    { field: "chatIdSource", message: "Chat ID source is required" },
    { field: "messageIdSource", message: "Message ID source is required" },
    { field: "instructions", message: "Parse prompt is required" }
  ],
  platformListenerTrigger: [{ field: "platformEventType", message: "Please select an event type" }],
  recordScore: [
    { field: "metricName", message: "Metric name is required" },
    { field: "valueSource", message: "Value is required" },
    {
      field: "chatIdSource",
      message: "Conversation to score is required",
      when: (data) => scoreTargetRequiresChatId(data.scorableTarget)
    }
  ],
  sendSatisfactionPrompt: [
    { field: "chatIdSource", message: "Chat to prompt is required" },
    { field: "instructions", message: "Message template is required" }
  ]
};

/**
 * Every {@link REQUIRED_NODE_FIELDS} entry this node leaves empty, in table order.
 *
 * Empty for a node type the table does not name, so both callers can invoke it for
 * EVERY node rather than gating on membership. That is what keeps a new row from
 * needing a second edit on either side.
 */
export function missingRequiredNodeFields(
  nodeType: string,
  data: Record<string, unknown>
): RequiredNodeField[] {
  const required = REQUIRED_NODE_FIELDS[nodeType];
  if (!required) return [];
  return required.filter(
    (entry) => (entry.when ? entry.when(data) : true) && !hasValue(data[entry.field])
  );
}
